import { SystemPageKey } from '@internal/page/SystemPageKey';
import { KnownBaleChamberType } from '@internal/utils/machine/KnownBalerChamberType';
import { KnownMachineHeaderType } from '@internal/utils/machine/KnownMachineHeaderType';
import { MachineAttribute } from '@internal/utils/machine/MachineAttribute';
import {
    canHaveBaleChamberFilter,
    canHaveHeaderTypeFilter,
    isValidMachineSubType,
    isValidMachineType,
} from '@internal/utils/machine/MachineType';
import { useMachineTypeName } from '@internal/utils/machine/useMachineTypeName';
import {
    hotDealsRoute,
    matchSearchRoute,
} from '@internal/utils/routing/routingTable';
import { urlFor } from '@internal/utils/routing/urls/urlFor';
import { TFilterableBaleChamberOption } from '@internal/utils/search/TFilterableBalerChamberOption';
import { TFilterableHeaderTypeOption } from '@internal/utils/search/TFilterableHeaderTypeOption';
import isEqual from 'lodash/isEqual';
import { useRouter } from 'next/router';
import { usePageContext } from 'page/PageContext';
import { ParsedUrlQuery } from 'querystring';
import { useEffect, useMemo, useState } from 'react';
import { PageTypeKey } from 'utils/page/PageTypeKey';
import {
    isValidBaleChamberOption,
    isValidHeaderTypeOption,
} from '../../utils/search/isValidBaleChamberOption';
import { getCleanMachineSubType } from './getCleanMachineType';
import { isValidSortValue } from './isValidSortValue';
import { loadChunkSize } from './loadChunkSize';
import { SearchResultsSortKey } from './SearchResultsSortKey';
import { TFiltersConfiguration } from './TFiltersConfiguration';
import { TRangeFiltersMap } from './TRangeFiltersMap';
import {
    mappedModelSuffix,
    SearchModel,
    SearchTag,
    SearchTagsSchema,
    TSearchParams,
} from './TSearchParams';

const queryParamToMachineAttribute = (param: string) =>
    param.toUpperCase().replace(/-/g, '_') as MachineAttribute;

const toArray = (items: string | string[] | undefined | null): string[] =>
    items ? (Array.isArray(items) ? items : [items]) : [];

export const getLocations = (items: string | string[] | undefined): string[] =>
    toArray(items).map(location => location.toUpperCase());

export const getFeatures = (items: string | string[] | undefined) =>
    toArray(items).map(queryParamToMachineAttribute);

export const getBaleChamber = (option: string): TFilterableBaleChamberOption =>
    isValidBaleChamberOption(option)
        ? option
        : KnownBaleChamberType.FIXED_CHAMBER;

export const getHeaderType = (option: string): TFilterableHeaderTypeOption =>
    isValidHeaderTypeOption(option) ? option : KnownMachineHeaderType.HEADER;

export const getSort = (item: string | string[] | undefined) => {
    const sort = `${item}` as SearchResultsSortKey;
    return isValidSortValue(sort) ? sort : SearchResultsSortKey.RelevanceAsc;
};

export const getRangeFiltersMap = (queryParams: ParsedUrlQuery) => {
    return Object.keys(queryParams).reduce<TRangeFiltersMap>((map, key) => {
        const attribute = queryParamToMachineAttribute(key);
        const value = (queryParams[key] ?? '').toString();

        const values = value.replace('x', '').split('-');
        const minValue = parseInt(values[0] ?? '');
        const maxValue = parseInt(values[1] ?? '');
        const min = isNaN(minValue) ? undefined : minValue;
        const max = isNaN(maxValue) ? undefined : maxValue;

        if (min || max) {
            map[attribute] = {
                min,
                max,
            };
        }
        return map;
    }, {});
};

export type SearchParamsResult = TSearchParams & {
    isOnMachineCategoryPage: boolean;
};

export const useSearchParams = () => {
    const { locale, pageData } = usePageContext();
    const {
        query: {
            brand: queryBrand,
            model: queryModel,
            baseModel: queryBaseModel,
            modelSeries: queryModelSeries,
            machineSubType: queryMachineSubType,
            headerType: queryHeaderType,
            baleChamber: queryBaleChamber,
            amount: queryAmount,
            feature: queryFeature,
            location: queryLocation,
            sort: querySort,
            tag: queryTag,
            machineType: queryMachineType,
            ...queryRangeFilters
        },
        asPath,
    } = useRouter();

    const url = urlFor(locale, asPath.split('?')[0]);

    const { getMachineTypeForMachineTypeSlug } = useMachineTypeName();
    const { pathMachineType, pathTag } = useMemo(() => {
        const routeMatchResult = matchSearchRoute(locale, url);
        const machineTypeSlug = routeMatchResult?.machineTypeSlug ?? null;

        if (machineTypeSlug === hotDealsRoute[locale]) {
            return {
                pathMachineType: null,
                pathTag: SearchTag.HotDeals,
            };
        }

        return {
            pathMachineType: machineTypeSlug
                ? getMachineTypeForMachineTypeSlug(machineTypeSlug)
                : null,
            pathTag: null,
        };
    }, [locale, url, getMachineTypeForMachineTypeSlug]);
    const machineType =
        pathMachineType ??
        (isValidMachineType(queryMachineType) ? queryMachineType : null) ??
        null;

    const machineCategoryPageParams = useMemo(() => {
        if (
            pageData?.type !== PageTypeKey.SystemPage ||
            pageData.key !== SystemPageKey.SearchResults ||
            !pageData.machineCategoryData ||
            pageData.machineCategoryData?.url !== url
        ) {
            return null;
        }

        if (pageData.machineCategoryData.model && !queryModel) {
            return {
                model: pageData.machineCategoryData.model,
                brand: pageData.machineCategoryData.brand,
            };
        }

        if (pageData.machineCategoryData.brand && !queryBrand) {
            return {
                model: pageData.machineCategoryData.model,
                brand: pageData.machineCategoryData.brand,
            };
        }

        // when we have category data, and not query params filters, apply mcp data
        if (!queryModel && !queryBrand) {
            return {
                model: pageData.machineCategoryData.model,
                brand: pageData.machineCategoryData.brand,
            };
        }

        return null;
    }, [pageData, url, queryModel, queryBrand]);

    const parsedAmount = parseInt(`${queryAmount}`);
    const amount = isNaN(parsedAmount) ? loadChunkSize : parsedAmount;

    // BRANDS
    const [brands, setBrands] = useState<string[]>(
        toArray(
            machineCategoryPageParams
                ? machineCategoryPageParams.brand
                : queryBrand,
        ),
    );

    useEffect(() => {
        const newBrands = toArray(
            machineCategoryPageParams
                ? machineCategoryPageParams.brand
                : queryBrand,
        );
        if (!isEqual(newBrands, brands)) {
            setBrands(newBrands);
        }
    }, [brands, queryBrand, pageData, machineCategoryPageParams]);

    // LOCATIONS
    const [locations, setLocations] = useState<string[]>(
        getLocations(queryLocation),
    );
    useEffect(() => {
        const newLocations = getLocations(queryLocation);
        if (!isEqual(newLocations, locations)) {
            setLocations(newLocations);
        }
    }, [locations, queryLocation]);

    // FEATURES
    const [features, setFeatures] = useState<MachineAttribute[]>(
        getFeatures(queryFeature),
    );
    useEffect(() => {
        const newFeatures = getFeatures(queryFeature);
        if (!isEqual(newFeatures, features)) {
            setFeatures(newFeatures);
        }
    }, [features, queryFeature]);

    // RANGE FILTERS
    const [rangeFilters, setRangeFilters] = useState<TRangeFiltersMap>(
        getRangeFiltersMap(queryRangeFilters),
    );

    // BALE_CHAMBER
    const baleChamber = queryBaleChamber
        ? getBaleChamber(queryBaleChamber?.toString())
        : undefined;

    useEffect(() => {
        const newRangeFilters = getRangeFiltersMap(queryRangeFilters);
        if (!isEqual(newRangeFilters, rangeFilters)) {
            setRangeFilters(newRangeFilters);
        }
    }, [queryRangeFilters, rangeFilters]);

    // HEADER_TYPE
    const headerType = queryHeaderType
        ? getHeaderType(queryHeaderType?.toString())
        : undefined;

    // MACHINE_SUB_TYPE
    const machineSubType = useMemo(() => {
        return machineType
            ? getCleanMachineSubType(queryMachineSubType, machineType)
            : null;
    }, [machineType, queryMachineSubType]);

    // MODEL
    const models = useMemo(() => {
        if (machineCategoryPageParams) {
            if (machineCategoryPageParams.model) {
                return [machineCategoryPageParams.model];
            }
            return [];
        }

        let models: SearchModel[] = [];

        if (typeof queryModel === 'string' && queryModel.length > 0) {
            models.push({
                name: queryModel.replace(mappedModelSuffix, ''),
                type: queryModel.endsWith(mappedModelSuffix)
                    ? 'mapped'
                    : 'fuzzy',
            });
        } else if (Array.isArray(queryModel)) {
            models.push(
                ...queryModel.map(
                    (name): SearchModel => ({
                        name: name.replace(mappedModelSuffix, ''),
                        type: name.endsWith(mappedModelSuffix)
                            ? 'mapped'
                            : 'fuzzy',
                    }),
                ),
            );
        }

        if (typeof queryBaseModel === 'string' && queryBaseModel.length > 0) {
            models.push({
                name: queryBaseModel,
                type: 'base',
            });
        } else if (Array.isArray(queryBaseModel)) {
            models.push(
                ...queryBaseModel.map(
                    (name): SearchModel => ({
                        name,
                        type: 'base',
                    }),
                ),
            );
        }

        if (
            typeof queryModelSeries === 'string' &&
            queryModelSeries.length > 0
        ) {
            models.push({
                name: queryModelSeries,
                type: 'series',
            });
        } else if (Array.isArray(queryModelSeries)) {
            models.push(
                ...queryModelSeries.map(
                    (name): SearchModel => ({
                        name,
                        type: 'series',
                    }),
                ),
            );
        }

        return models;
    }, [
        machineCategoryPageParams,
        queryBaseModel,
        queryModel,
        queryModelSeries,
    ]);

    // TAGS
    const [tags, setTags] = useState<SearchTag[]>(
        pathTag
            ? [pathTag]
            : (SearchTagsSchema.safeParse(toArray(queryTag)).data ?? []),
    );
    useEffect(() => {
        const newTags =
            (queryTag
                ? SearchTagsSchema.safeParse(toArray(queryTag)).data
                : null) ??
            (pathTag ? [pathTag] : null) ??
            [];
        if (!isEqual(newTags, tags)) {
            setTags(newTags);
        }
    }, [tags, queryTag, pathTag]);

    // SORTING
    const sort = useMemo(() => {
        if (querySort) {
            return getSort(querySort);
        }
        if (tags.includes(SearchTag.HotDeals)) {
            return SearchResultsSortKey.Latest;
        }

        return SearchResultsSortKey.RelevanceAsc;
    }, [querySort, tags]);

    const params = useMemo<SearchParamsResult>(
        () => ({
            tags,
            models,
            brands,
            machineType: machineType ?? undefined,
            machineSubType: machineSubType ?? undefined,
            headerType,
            baleChamber,
            amount,
            features,
            rangeFilters,
            locations,
            sort,
            isOnMachineCategoryPage: !!machineCategoryPageParams,
        }),
        [
            tags,
            models,
            brands,
            machineType,
            machineSubType,
            headerType,
            baleChamber,
            amount,
            features,
            rangeFilters,
            locations,
            sort,
            machineCategoryPageParams,
        ],
    );

    return params;
};

export const getSearchParamsCount = (searchParams: TSearchParams) => {
    return (
        searchParams.brands.length +
        searchParams.models.length +
        (searchParams.machineType ? 1 : 0) +
        (searchParams.machineSubType ? 1 : 0) +
        (searchParams.headerType ? 1 : 0) +
        (searchParams.baleChamber ? 1 : 0) +
        searchParams.tags.length +
        searchParams.features.length +
        Object.keys(searchParams.rangeFilters).length +
        searchParams.locations.length
    );
};

export const getHasAnySearchParams = (searchParams: TSearchParams) =>
    getSearchParamsCount(searchParams) > 0;

export const sanitizeSearchParams = (
    searchParams: TSearchParams,
    filters: TFiltersConfiguration,
) => {
    const rangeFilters = Object.entries(
        searchParams.rangeFilters,
    ).reduce<TRangeFiltersMap>((map, [attribute, value]) => {
        if (filters.rangeFilters.some(f => f.machineAttribute === attribute)) {
            map[attribute] = value;
        }
        return map;
    }, {});

    const sanitizedSearchParams: TSearchParams = {
        ...searchParams,
        machineSubType: searchParams.machineType
            ? isValidMachineSubType(
                  searchParams.machineSubType,
                  searchParams.machineType,
              )
                ? searchParams.machineSubType
                : undefined
            : undefined,
        rangeFilters,
        features: searchParams.features.filter(f =>
            filters.features.some(ff => ff.machineAttribute === f),
        ),
        headerType: canHaveHeaderTypeFilter(searchParams.machineType)
            ? searchParams.headerType
            : undefined,
        baleChamber: canHaveBaleChamberFilter(searchParams.machineType)
            ? searchParams.baleChamber
            : undefined,
    };
    return sanitizedSearchParams;
};
